import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldUser } from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../app/providers/AuthProvider";
import PageHeader from "../../components/common/PageHeader";
import PlatformActorListCard from "../../components/common/PlatformActorListCard";
import {
  PASSWORD_POLICY_MESSAGE,
  validatePasswordPolicy,
} from "../../utils/passwordPolicy";

const CREATE_PARTNER_ENDPOINT = "/platform-onboarding/partners";
const PARTNER_LOOKUP_ENDPOINT = "/platform-onboarding/partners/lookup";

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.message ||
    fallbackMessage
  );
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function extractList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.payload)) return data.payload;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export default function CreatePartnerPage() {
  const { role, roleKey } = useAuth();
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    status: "Active",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partners, setPartners] = useState([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);
  const [listError, setListError] = useState("");

  const normalizedRoleKey = useMemo(() => {
    const rawRole = String(role || "").trim().toLowerCase();
    const rawRoleKey = String(roleKey || "").trim().toLowerCase();

    if (rawRoleKey) return rawRoleKey;
    if (rawRole === "admin" || rawRole === "superadmin") {
      return "platform.super_admin";
    }
    if (rawRole === "partner") return "platform.partner";
    if (rawRole === "reseller") return "platform.reseller";
    return "";
  }, [role, roleKey]);

  const canCreatePartners = normalizedRoleKey === "platform.super_admin";

  const resetForm = () => {
    setForm({
      fullName: "",
      email: "",
      password: "",
      status: "Active",
    });
    setFieldErrors({});
    setSubmitError("");
  };

  const loadPartners = useCallback(async () => {
    if (!canCreatePartners) return;

    try {
      setIsLoadingPartners(true);
      setListError("");

      const response = await axiosClient.get(PARTNER_LOOKUP_ENDPOINT, {
        __skipBizHeader: true,
      });

      setPartners(extractList(response.data));
    } catch (error) {
      setListError(getErrorMessage(error, "Failed to load partners."));
    } finally {
      setIsLoadingPartners(false);
    }
  }, [canCreatePartners]);

  useEffect(() => {
    if (!canCreatePartners) {
      toast.error("You do not have access to create partners.");
    }
  }, [canCreatePartners]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
    setFieldErrors(current => ({ ...current, [name]: "" }));
    setSubmitError("");
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.fullName.trim()) {
      nextErrors.fullName = "Full name is required.";
    }

    if (!form.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!isValidEmail(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    const passwordError = validatePasswordPolicy(form.password);
    if (passwordError) {
      nextErrors.password = passwordError;
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      setSubmitError("");

      await axiosClient.post(
        CREATE_PARTNER_ENDPOINT,
        {
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          password: form.password,
          status: form.status,
        },
        {
          __skipBizHeader: true,
        }
      );

      toast.success("Partner created successfully");
      resetForm();
      await loadPartners();
    } catch (error) {
      const message = getErrorMessage(error, "Failed to create partner.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (name, label, type = "text", autoComplete) => (
    <label className="space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={handleChange}
        autoComplete={autoComplete}
        className={[
          "w-full rounded-[10px] border bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition",
          fieldErrors[name]
            ? "border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
            : "border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100",
        ].join(" ")}
      />
      {fieldErrors[name] ? (
        <div className="text-xs font-medium text-rose-600">{fieldErrors[name]}</div>
      ) : null}
    </label>
  );

  const renderSelect = (name, label, options) => (
    <label className="space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
      <select
        name={name}
        value={form[name]}
        onChange={handleChange}
        className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );

  if (!canCreatePartners) {
    return <Navigate to="/app/admin/approvals" replace />;
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6] py-6">
      <section className="px-4">
        <div className="mx-auto max-w-[1120px] space-y-6">
          <div className="px-4 pt-4 pb-1">
            <PageHeader
              icon={ShieldUser}
              title="Create Partner"
              subtitle="Create a managed partner and review existing partner accounts in one place"
            />
          </div>

          <div className="px-4">
            <div className="rounded-[10px] border border-slate-200 bg-white shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-6 px-5 py-5">
                {submitError ? (
                  <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {submitError}
                  </div>
                ) : null}

                <div className="grid gap-4 md:grid-cols-2">
                  {renderField("fullName", "Full Name", "text", "name")}
                  {renderField("email", "Email", "email", "email")}
                  {renderField("password", "Password", "password", "new-password")}
                  {renderSelect("status", "Account Status", [
                    { value: "Active", label: "Active" },
                    { value: "Hold", label: "Hold" },
                  ])}
                </div>

                <div className="rounded-[10px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  {PASSWORD_POLICY_MESSAGE}
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 rounded-[10px] bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Partner"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="px-4">
            <PlatformActorListCard
              title="Existing Partners"
              subtitle="Review partner accounts and their current account status."
              actors={partners}
              loading={isLoadingPartners}
              error={listError}
              emptyMessage="No partners have been created yet."
            />
          </div>
        </div>
      </section>
    </div>
  );
}
